import { useEffect, useRef, useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, useTheme, useMediaQuery } from '@mui/material';
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker, ScaleControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to fit bounds when data changes
function FitBounds({ bounds }) {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);

  return null;
}

// Format elapsed seconds as hh:mm:ss
function formatElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Generic zoom-aware point layer — only renders when zoomed in (zoom >= 15)
// Filters to viewport bounds to avoid rendering thousands of off-screen markers.
function ZoomPointsLayer({ points, keyPrefix, radius, color, fillColor, popupBuilder }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const [bounds, setBounds] = useState(map.getBounds());

  useMapEvents({
    zoomend: () => { setZoom(map.getZoom()); setBounds(map.getBounds()); },
    moveend: () => setBounds(map.getBounds()),
  });

  if (zoom < 15 || !points || points.length === 0) return null;

  const visible = points
    .map((pt, idx) => ({ pt, idx }))
    .filter(({ pt }) => pt.lat && pt.lon && bounds.contains([pt.lat, pt.lon]));

  return visible.map(({ pt, idx }) => (
    <CircleMarker
      key={`${keyPrefix}-${idx}`}
      center={[pt.lat, pt.lon]}
      radius={radius || 3}
      pathOptions={{ color: color || '#fff', fillColor: fillColor || '#90caf9', fillOpacity: 0.9, weight: 1 }}
    >
      <Popup>{popupBuilder(pt, idx)}</Popup>
    </CircleMarker>
  ));
}

// Climb category → difficulty colour (yellow → orange → red, hardest = darkest red)
const CLIMB_MAP_COLORS = {
  'R-C4': '#ffe066',  // light yellow
  'R-C3': '#ffa020',  // amber / orange
  'R-C2': '#e05000',  // dark orange
  'R-C1': '#c01010',  // red
  'R-HC': '#7a0000',  // deep red
};

// Descent category → blue-to-violet gradient palette
const DESCENT_MAP_COLORS = {
  'R-D4':  '#8ec5f5',  // sky blue
  'R-D3':  '#6898e8',  // cornflower blue
  'R-D2':  '#6b6ee0',  // blue-violet
  'R-D1':  '#8c48cc',  // indigo-violet
  'R-DHC': '#be35b8',  // vivid violet
};

const RunningRouteMap = ({ chunks, race, matchedCheckpoints = [], planBoundaryMarkers = [], title = 'Route Map', visibleDistanceRange = null, gpxRoutePoints = null, originalFitPoints = null, climbHighlights = [], descentHighlights = [], segmentHighlight = null, liveMarker = null, predictedMarker = null, headerExtra = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [showOriginalFit, setShowOriginalFit] = useState(false);

  // On phones, shrink the marker circles but keep the glyph inside relatively large
  // (less padding), so markers take up less map real estate without becoming illegible.
  const cpOuterSize = isMobile ? 22 : 32;
  const cpInnerSize = isMobile ? 15 : 16;
  const cpBorder = isMobile ? 1.5 : 2;

  // Live/predicted position dots stay the same size as the checkpoint markers, scaled together.
  const liveBorder = isMobile ? 2 : 3;
  const livePulseSize = Math.round(cpOuterSize * 1.25);
  const livePulseOffset = (livePulseSize - cpOuterSize) / 2;

  // Extract GPS coordinates from chunks, split by is_moving flag.
  // movingCoords: normal route (blue), stoppedCoords: not-moving samples (light blue).
  // Each contiguous run of same-type points forms one segment to avoid
  // drawing lines that skip across the map between disconnected groups.
  const movingSegments = [];   // array of [[lat,lon], ...] runs
  const stoppedSegments = [];  // array of [[lat,lon], ...] runs
  const dataPoints = [];       // all visible points with metadata for click inspection
  const allPoints = [];        // all points unfiltered — used for climb highlights

  if (chunks) {
    let curMoving = [];
    let curStopped = [];

    const flushMoving = () => { if (curMoving.length >= 1) movingSegments.push(curMoving); curMoving = []; };
    const flushStopped = () => { if (curStopped.length >= 1) stoppedSegments.push(curStopped); curStopped = []; };

    chunks.forEach(chunk => {
      if (!chunk.points || !Array.isArray(chunk.points)) return;
      chunk.points.forEach(pt => {
        if (!pt.lat || !pt.lon) return;

        // Collect all points unfiltered for climb highlight polylines
        allPoints.push(pt);

        // Filter by visible distance range if set (cumulative_distance is in meters)
        if (visibleDistanceRange) {
          const distKm = (pt.cumulative_distance || 0) / 1000;
          if (distKm < visibleDistanceRange.leftKm || distKm > visibleDistanceRange.rightKm) {
            flushMoving();
            flushStopped();
            return;
          }
        }

        const coord = [pt.lat, pt.lon];
        dataPoints.push(pt);
        if (pt.is_moving === false) {
          // Transition: flush moving run, continue stopped run
          if (curMoving.length > 0) { curMoving.push(coord); flushMoving(); }
          curStopped.push(coord);
        } else {
          // Transition: flush stopped run, continue moving run
          if (curStopped.length > 0) { curStopped.push(coord); flushStopped(); }
          curMoving.push(coord);
        }
      });
    });
    flushMoving();
    flushStopped();
  }

  // Fallback: all coordinates for bounds calculation
  const routeCoordinates = [...movingSegments, ...stoppedSegments].flat();

  // Debug logging
  console.log('[RouteMap] Debug info:', {
    chunksCount: chunks ? chunks.length : 0,
    allPointsCount: allPoints.length,
    dataPointsCount: dataPoints.length,
    routeCoordsCount: routeCoordinates.length,
    lastAllPoint: allPoints.length > 0 ? allPoints[allPoints.length - 1] : null,
    lastDataPoint: dataPoints.length > 0 ? dataPoints[dataPoints.length - 1] : null,
    lastRouteCoord: routeCoordinates.length > 0 ? routeCoordinates[routeCoordinates.length - 1] : null,
  });

  if (routeCoordinates.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: colors.grey[800], borderRadius: '8px' }}>
        <Typography color={colors.grey[300]}>
          No GPS data available for map
        </Typography>
      </Box>
    );
  }

  // Calculate center point
  const center = [
    routeCoordinates.reduce((sum, coord) => sum + coord[0], 0) / routeCoordinates.length,
    routeCoordinates.reduce((sum, coord) => sum + coord[1], 0) / routeCoordinates.length
  ];

  // Get start and finish markers
  // Use allPoints (chronologically ordered) instead of routeCoordinates
  // (which concatenates movingSegments + stoppedSegments, breaking chronological order)
  const start = allPoints.length > 0 ? [allPoints[0].lat, allPoints[0].lon] : routeCoordinates[0];
  const finish = allPoints.length > 0 ? [allPoints[allPoints.length - 1].lat, allPoints[allPoints.length - 1].lon] : routeCoordinates[routeCoordinates.length - 1];

  console.log('[RouteMap] Markers:', {
    start,
    finish,
    routeCoordsFinish: routeCoordinates[routeCoordinates.length - 1],
  });

  // Split into detected checkpoints, undetected checkpoints, and unplanned stops
  const checkpointMarkers = matchedCheckpoints
    .filter(cp => cp.status !== 'undetected' && cp.fit_lat != null && cp.fit_lon != null && cp.type === 'checkpoint')
    .map(cp => ({ position: [cp.fit_lat, cp.fit_lon], name: cp.name, distance: cp.fit_distance_km }));

  // Undetected: use the checkpoint's own lat/lon from race data (not fit position)
  const undetectedMarkers = matchedCheckpoints
    .filter(cp => cp.status === 'undetected' && cp.lat != null && cp.lon != null && cp.type === 'checkpoint')
    .map(cp => ({ position: [cp.lat, cp.lon], name: cp.name, distance: cp.distance_km }));

  const unplannedStopMarkers = matchedCheckpoints
    .filter(cp => cp.type === 'unplanned_stop' && cp.fit_lat != null && cp.fit_lon != null)
    .map(cp => ({ position: [cp.fit_lat, cp.fit_lon], name: cp.name, distance: cp.fit_distance_km,
                  stopping_time_seconds: cp.stopping_time_seconds }));

  // Styled timer icon for detected race checkpoints (purple gradient)
  const checkpointIcon = L.divIcon({
    className: 'checkpoint-marker',
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${cpOuterSize}px;
        height: ${cpOuterSize}px;
        background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
        border: ${cpBorder}px solid #fff;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <svg width="${cpInnerSize}" height="${cpInnerSize}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
          <circle cx="12" cy="12" r="9"/>
          <polyline points="12 6 12 12 15 15"/>
        </svg>
      </div>
    `,
    iconSize: [cpOuterSize, cpOuterSize],
    iconAnchor: [cpOuterSize / 2, cpOuterSize / 2],
    popupAnchor: [0, -cpOuterSize / 2],
  });

  // Styled timer icon for undetected checkpoints (red gradient)
  const undetectedIcon = L.divIcon({
    className: 'undetected-checkpoint-marker',
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${cpOuterSize}px;
        height: ${cpOuterSize}px;
        background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
        border: ${cpBorder}px solid #fff;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <svg width="${cpInnerSize}" height="${cpInnerSize}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
          <circle cx="12" cy="12" r="9"/>
          <polyline points="12 6 12 12 15 15"/>
        </svg>
      </div>
    `,
    iconSize: [cpOuterSize, cpOuterSize],
    iconAnchor: [cpOuterSize / 2, cpOuterSize / 2],
    popupAnchor: [0, -cpOuterSize / 2],
  });

  // Styled timer icon for unplanned stops (orange gradient)
  const unplannedStopIcon = L.divIcon({
    className: 'unplanned-stop-marker',
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${cpOuterSize}px;
        height: ${cpOuterSize}px;
        background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
        border: ${cpBorder}px solid #fff;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <svg width="${cpInnerSize}" height="${cpInnerSize}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
          <circle cx="12" cy="12" r="9"/>
          <polyline points="12 6 12 12 15 15"/>
        </svg>
      </div>
    `,
    iconSize: [cpOuterSize, cpOuterSize],
    iconAnchor: [cpOuterSize / 2, cpOuterSize / 2],
    popupAnchor: [0, -cpOuterSize / 2],
  });

  // Live "last known position" marker — pulsing red dot, sized to match the checkpoint icons
  const liveIcon = L.divIcon({
    className: 'live-position-marker',
    html: `
      <div style="position: relative; width: ${livePulseSize}px; height: ${livePulseSize}px;">
        <div style="
          position: absolute; inset: 0;
          border-radius: 50%;
          background: rgba(244, 67, 54, 0.35);
          animation: mkpace-live-pulse 1.6s ease-out infinite;
        "></div>
        <div style="
          position: absolute; top: ${livePulseOffset}px; left: ${livePulseOffset}px;
          width: ${cpOuterSize}px; height: ${cpOuterSize}px;
          background: #f44336;
          border: ${liveBorder}px solid #fff;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        "></div>
      </div>
      <style>
        @keyframes mkpace-live-pulse {
          0% { transform: scale(0.6); opacity: 0.9; }
          100% { transform: scale(1.5); opacity: 0; }
        }
      </style>
    `,
    iconSize: [livePulseSize, livePulseSize],
    iconAnchor: [livePulseSize / 2, livePulseSize / 2],
    popupAnchor: [0, -livePulseSize / 2],
  });

  // Predicted "where they should be right now" marker — solid blue, no pulse (distinguishes
  // a projection from the confirmed-red last-known-position dot; same red/blue pairing as the chart),
  // sized to match the checkpoint icons
  const predictedIcon = L.divIcon({
    className: 'predicted-position-marker',
    html: `
      <div style="
        width: ${cpOuterSize}px; height: ${cpOuterSize}px;
        background: #2196f3;
        border: ${liveBorder}px solid #fff;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      "></div>
    `,
    iconSize: [cpOuterSize, cpOuterSize],
    iconAnchor: [cpOuterSize / 2, cpOuterSize / 2],
    popupAnchor: [0, -cpOuterSize / 2],
  });

  return (
    <Box>
      {/* One header row — title + race on the left, map toggles on the right. Keeps the space
          above the map to a single line instead of three or four stacked rows. */}
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap" minWidth={0}>
          <Typography variant="h6" color={colors.grey[100]}>
            {title}
          </Typography>
          {race && (
            <Typography variant="body2" color={colors.grey[300]} noWrap>
              {race.name}{race.event_name ? ` - ${race.event_name}` : ''}
            </Typography>
          )}
        </Box>
        {(headerExtra || (originalFitPoints && originalFitPoints.length > 0)) && (
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
            {headerExtra}
            {originalFitPoints && originalFitPoints.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOriginalFit}
                    onChange={(e) => setShowOriginalFit(e.target.checked)}
                    size="small"
                    sx={{ color: '#ff5252', '&.Mui-checked': { color: '#ff5252' } }}
                  />
                }
                label={<Typography variant="body2" color={colors.grey[300]}>{t('showOriginalFit', 'Show original FIT (before sanitization/snap)')}</Typography>}
                sx={{ mr: 0 }}
              />
            )}
          </Box>
        )}
      </Box>
      {(liveMarker || predictedMarker) && (
        <Box display="flex" gap={2.5} flexWrap="wrap" mb={1}>
          {liveMarker && (
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#f44336', border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.3)' }} />
              <Typography variant="caption" color={colors.grey[300]}>Last known position</Typography>
            </Box>
          )}
          {predictedMarker && (
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2196f3', border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.3)' }} />
              <Typography variant="caption" color={colors.grey[300]}>Predicted current position</Typography>
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          height: 400,
          borderRadius: '8px',
          overflow: 'hidden',
          '& .leaflet-container': {
            height: '100%',
            backgroundColor: colors.grey[900]
          }
        }}
      >
        <MapContainer
          center={center}
          zoom={13}
          maxZoom={19}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            maxZoom={19}
          />

          {/* Original FIT route — red dashed, rendered underneath everything */}
          {showOriginalFit && originalFitPoints && originalFitPoints.length > 0 && (
            <Polyline
              positions={originalFitPoints.map(pt => [pt.lat, pt.lon])}
              pathOptions={{ color: '#ff5252', weight: 2, opacity: 0.7, dashArray: '6, 4' }}
            />
          )}

          {/* GPX course route — dashed magenta, rendered underneath FIT route */}
          {gpxRoutePoints && gpxRoutePoints.length > 0 && (
            <Polyline
              positions={gpxRoutePoints.map(pt => [pt.lat, pt.lon])}
              pathOptions={{ color: '#e040fb', weight: 3, opacity: 0.7, dashArray: '8, 6' }}
            />
          )}

          {/* Moving segments — normal blue */}
          {movingSegments.map((seg, i) => (
            <Polyline
              key={`moving-${i}`}
              positions={seg}
              pathOptions={{ color: colors.blueAccent[500], weight: 4, opacity: 0.85 }}
            />
          ))}

          {/* Not-moving segments — light blue, thinner, to highlight GPS artefacts */}
          {stoppedSegments.map((seg, i) => (
            <Polyline
              key={`stopped-${i}`}
              positions={seg}
              pathOptions={{ color: '#42a5f5', weight: 3, opacity: 0.75 }}
            />
          ))}

          {/* Climb highlight polylines — rendered on top of the route */}
          {climbHighlights && climbHighlights.map((climb, i) => {
            const startM = climb.start_distance_m;
            const endM   = climb.end_distance_m;
            const coords = allPoints
              .filter(pt => {
                const d = pt.cumulative_distance || 0;
                return d >= startM && d <= endM && pt.lat && pt.lon;
              })
              .map(pt => [pt.lat, pt.lon]);
            if (coords.length < 2) return null;
            const color = CLIMB_MAP_COLORS[climb.category] || CLIMB_MAP_COLORS['R-C4'];
            return (
              <Polyline
                key={`climb-hl-${i}`}
                positions={coords}
                pathOptions={{ color, weight: 6, opacity: 0.75 }}
              />
            );
          })}

          {/* Descent highlight polylines — rendered on top of the route */}
          {descentHighlights && descentHighlights.map((descent, i) => {
            const startM = descent.start_distance_m;
            const endM   = descent.end_distance_m;
            const coords = allPoints
              .filter(pt => {
                const d = pt.cumulative_distance || 0;
                return d >= startM && d <= endM && pt.lat && pt.lon;
              })
              .map(pt => [pt.lat, pt.lon]);
            if (coords.length < 2) return null;
            const color = DESCENT_MAP_COLORS[descent.category] || DESCENT_MAP_COLORS['R-D4'];
            return (
              <Polyline
                key={`descent-hl-${i}`}
                positions={coords}
                pathOptions={{ color, weight: 6, opacity: 0.75 }}
              />
            );
          })}

          {/* Segment highlight — bright yellow, rendered on top of climb/descent highlights */}
          {segmentHighlight && (() => {
            const startM = segmentHighlight.start_distance_m ?? (segmentHighlight.leftKm * 1000);
            const endM   = segmentHighlight.end_distance_m   ?? (segmentHighlight.rightKm * 1000);
            const coords = allPoints
              .filter(pt => {
                const d = pt.cumulative_distance || 0;
                return d >= startM && d <= endM && pt.lat && pt.lon;
              })
              .map(pt => [pt.lat, pt.lon]);
            if (coords.length < 2) return null;
            return (
              <Polyline
                positions={coords}
                pathOptions={{ color: '#ffeb3b', weight: 7, opacity: 0.92 }}
              />
            );
          })()}

          {/* Start marker */}
          <Marker position={start}>
            <Popup>
              <Typography variant="body2" fontWeight="bold">Start</Typography>
            </Popup>
          </Marker>

          {/* Finish marker */}
          <Marker position={finish}>
            <Popup>
              <Typography variant="body2" fontWeight="bold">Finish</Typography>
            </Popup>
          </Marker>

          {/* Predicted "where they should be right now" marker — blue, drawn under the red one */}
          {predictedMarker && predictedMarker.lat != null && predictedMarker.lon != null && (
            <Marker position={[predictedMarker.lat, predictedMarker.lon]} icon={predictedIcon} zIndexOffset={900}>
              <Popup>
                <Typography variant="body2" fontWeight="bold" style={{ color: '#2196f3' }}>
                  {predictedMarker.label || 'Predicted current position'}
                </Typography>
                {predictedMarker.sublabel && (
                  <Typography variant="caption" display="block">{predictedMarker.sublabel}</Typography>
                )}
              </Popup>
            </Marker>
          )}

          {/* Live "last known position" marker */}
          {liveMarker && liveMarker.lat != null && liveMarker.lon != null && (
            <Marker position={[liveMarker.lat, liveMarker.lon]} icon={liveIcon} zIndexOffset={1000}>
              <Popup>
                <Typography variant="body2" fontWeight="bold" style={{ color: '#f44336' }}>
                  {liveMarker.label || 'Last known position'}
                </Typography>
                {liveMarker.sublabel && (
                  <Typography variant="caption" display="block">{liveMarker.sublabel}</Typography>
                )}
              </Popup>
            </Marker>
          )}

          {/* Race checkpoint markers (green) */}
          {/* Plan boundaries — the same places the altitude chart marks, from the
              shared helper, so a checkpoint the user placed appears here too and
              not only on the chart. Drawn first so aid stations stay on top. */}
          {planBoundaryMarkers.map((b, idx) => (
            <CircleMarker
              key={`pb-${idx}`}
              center={b.position}
              radius={7}
              // markerPane sits above overlayPane, where the track polylines
              // live. Relying on JSX order is not enough: the track is drawn as
              // many polylines that React re-appends on re-render, so they land
              // back on top and bury these under the line they are marking.
              pane="markerPane"
              pathOptions={{
                color: '#ffffff',
                weight: 2,
                fillColor: b.manualId ? '#ab47bc' : '#fb923c',
                fillOpacity: 1,
              }}
            >
              <Popup>
                <Typography variant="body2" fontWeight="bold">{b.name}</Typography>
                <Typography variant="caption">{b.distance?.toFixed(1)} km</Typography>
                {b.manualId && (
                  <Typography variant="caption" display="block" style={{ color: '#ab47bc' }}>
                    Your checkpoint
                  </Typography>
                )}
              </Popup>
            </CircleMarker>
          ))}

          {checkpointMarkers.map((cp, idx) => (
            <Marker key={`cp-${idx}`} position={cp.position} icon={checkpointIcon}>
              <Popup>
                <Typography variant="body2" fontWeight="bold">{cp.name}</Typography>
                <Typography variant="caption">{cp.distance} km</Typography>
              </Popup>
            </Marker>
          ))}

          {/* Undetected checkpoint markers (red) — shown at official GPS position */}
          {undetectedMarkers.map((cp, idx) => (
            <Marker key={`undetected-${idx}`} position={cp.position} icon={undetectedIcon}>
              <Popup>
                <Typography variant="body2" fontWeight="bold" style={{ color: '#f44336' }}>{cp.name}</Typography>
                <Typography variant="caption">{cp.distance} km</Typography>
                <Typography variant="caption" display="block" style={{ color: '#f44336' }}>Not on course</Typography>
              </Popup>
            </Marker>
          ))}

          {/* Unplanned stop markers (orange) */}
          {unplannedStopMarkers.map((stop, idx) => (
            <Marker key={`stop-${idx}`} position={stop.position} icon={unplannedStopIcon}>
              <Popup>
                <Typography variant="body2" fontWeight="bold" style={{ color: '#ff9800' }}>{stop.name}</Typography>
                <Typography variant="caption">{stop.distance} km</Typography>
                {stop.stopping_time_seconds > 0 && (
                  <Typography variant="caption" display="block">
                    Stop: {Math.floor(stop.stopping_time_seconds / 60)}m {stop.stopping_time_seconds % 60}s
                  </Typography>
                )}
              </Popup>
            </Marker>
          ))}

          {/* Original FIT data points — red dots, rendered FIRST (underneath) */}
          {showOriginalFit && originalFitPoints && originalFitPoints.length > 0 && (
            <ZoomPointsLayer
              points={originalFitPoints}
              keyPrefix="orig"
              radius={3}
              color="#fff"
              fillColor="#ff5252"
              popupBuilder={(pt, idx) => (
                <>
                  <Typography variant="body2" fontWeight="bold" style={{ color: '#ff5252' }}>Original #{idx}</Typography>
                  <Typography variant="caption">{formatElapsed(pt.elapsed_seconds)}</Typography>
                  <Typography variant="caption" display="block">{((pt.cumulative_distance || 0) / 1000).toFixed(2)} km</Typography>
                  <Typography variant="caption" display="block">elev: {pt.altitude?.toFixed(0) ?? '?'}m</Typography>
                  {pt.heart_rate && <Typography variant="caption" display="block">HR: {pt.heart_rate} bpm</Typography>}
                  {pt.cadence && <Typography variant="caption" display="block">cad: {pt.cadence} spm</Typography>}
                </>
              )}
            />
          )}

          {/* GPX course data points — magenta dots when zoomed in */}
          {gpxRoutePoints && gpxRoutePoints.length > 0 && (
            <ZoomPointsLayer
              points={gpxRoutePoints}
              keyPrefix="gpx"
              radius={3}
              color="#fff"
              fillColor="#e040fb"
              popupBuilder={(pt, idx) => (
                <>
                  <Typography variant="body2" fontWeight="bold" style={{ color: '#e040fb' }}>GPX #{idx}</Typography>
                  <Typography variant="caption">{(pt.cumulative_distance / 1000).toFixed(2)} km</Typography>
                  <Typography variant="caption" display="block">elev: {pt.elevation?.toFixed(0) ?? '?'}m</Typography>
                </>
              )}
            />
          )}

          {/* Clickable FIT data points — visible when zoomed in, rendered LAST (on top) */}
          <ZoomPointsLayer
            points={dataPoints}
            keyPrefix="dp"
            radius={3}
            color="#fff"
            fillColor={colors.blueAccent[300]}
            popupBuilder={(pt) => (
              <>
                <Typography variant="body2" fontWeight="bold">{formatElapsed(pt.elapsed_seconds)}</Typography>
                <Typography variant="caption">{(pt.cumulative_distance / 1000).toFixed(2)} km</Typography>
              </>
            )}
          />

          <ScaleControl position="bottomleft" metric imperial={false} />

          {/* Fit bounds to show entire route */}
          <FitBounds bounds={routeCoordinates} />
        </MapContainer>
      </Box>
    </Box>
  );
};

export default RunningRouteMap;
